import * as THREE from "three";
import { MATS, tickWater } from "./materials.js";
import {
  BOUNDS, CREEK, MOVE, creekZ, greenPathZ, groundY, terraceY,
} from "./spec.js";
import { makeRng } from "./builder.js";

/**
 * The public greenspace behind the gardens: a strip of mown parkland with a
 * winding gravel trail and a small creek, thickening into scrubby woodland
 * along the back edge.  The wild counterpart to the tidy yards on the other
 * side of the fence.
 *
 * Composition runs front-to-back: open grass with wildflowers near the fence
 * line, the trail serpentining through the middle past a signpost,
 * the creek weaving along its woods side, then dense trees, brambles and
 * deadfall where the strip meets the woods.
 */

const PATH_W = 1.6;

/**
 * How far the bed at a point has been cut below the lawn beside it.
 *
 * This is the greenspace's depth gauge and it replaces every "distance from
 * the creek's centre line" test that used to live here.  The channel's
 * half-width and depth both vary along x -- pools and riffles -- so a fixed
 * radius is too generous over a bar and too mean through a pool, and the same
 * number means a different thing at every station.  A cut does not: it is 0
 * at the rim wherever the rim happens to be, and terraceY - CREEK.waterY
 * (0.27 m) at the waterline, everywhere, because the water is a level rather
 * than a depth.  One number, one meaning, the whole length of the creek.
 */
const cutAt = (x, z) => terraceY(z) - groundY(x, z);

/**
 * Build a multi-piece plant at ONE ground height: its own.
 *
 * MODULE_GRADE.greenspace is groundY, which the Builder samples at every
 * piece's own centre.  That is exactly right for a rock and quietly wrong for
 * a tree: a crown blob overhanging the creek samples the *bed* under it, drops
 * the depth of the channel out of the middle of its own canopy, and the tree
 * tears in half along the bank.  Pinning the grade to a number for the
 * duration is the case the Builder documents -- everything inside belongs to
 * one plant, so it all takes the level of the spot that plant grows out of.
 *
 * A no-op away from the creek, which is the only relief back here; it only
 * ever earns its keep for the things planted on the bank, and those are the
 * ones worth having.
 */
function rooted(b, x, z, build) {
  const grade = b.grade;
  b.setGrade(groundY(x, z));
  try { build(); } finally { b.setGrade(grade); }
}

// ---------------------------------------------------------------------------
// The winding gravel trail.
//
// greenPathZ() serpentines by ~4 m, so one long box cannot follow it.  We
// step along x and lay a short rotated plank per step, each aligned to the
// local tangent and slightly overlength so the joints disappear.  Adjacent
// planks alternate height by 2 mm because their overlapping tops are
// otherwise coplanar and z-fight.
//
// It is gravel, not paving, so nothing may draw a crisp line: the plank
// width wanders, pale wear strips sit where feet compact the middle, thin
// spill lobes push past the nominal edge at their own slight yaw, and loose
// stones dot the margins to break what outline is left.
function gravelPath(b, rng) {
  const step = 1.25;
  let i = 0;
  for (let x0 = BOUNDS.minX; x0 < BOUNDS.maxX; x0 += step, i++) {
    const x1 = Math.min(x0 + step, BOUNDS.maxX);
    const z0 = greenPathZ(x0), z1 = greenPathZ(x1);
    const dx = x1 - x0, dz = z1 - z0;
    const L = Math.hypot(dx, dz);
    const yaw = -Math.atan2(dz, dx);
    const mx = (x0 + x1) / 2, mz = (z0 + z1) / 2;
    const tx = dx / L, tz = dz / L;              // tangent
    const nx = -dz / L, nz = dx / L;             // cross-path normal
    const len = L + 0.3;
    const y = 0.006 + (i % 2) * 0.002;

    // base course, its width wandering a little
    const g = new THREE.BoxGeometry(len, 0.014, PATH_W + (rng() - 0.5) * 0.14);
    g.rotateY(yaw);
    g.translate(mx, y + 0.007, mz);
    b.add(g, MATS.concreteDark, { collider: null, ao: 0 });

    // buff wear strip, off-centre the way footfall actually is
    if (rng() < 0.7) {
      const w = new THREE.BoxGeometry(len, 0.006, 0.45 + rng() * 0.4);
      w.translate(0, 0, (rng() - 0.5) * 0.55);
      w.rotateY(yaw);
      w.translate(mx, y + 0.016, mz);
      b.add(w, MATS.stone, { collider: null, ao: 0 });
    }

    // spill lobes: gravel that has crept past the edge into the grass
    const lobes = rng() < 0.35 ? 2 : rng() < 0.75 ? 1 : 0;
    for (let l = 0; l < lobes; l++) {
      const side = rng() < 0.5 ? -1 : 1;
      const lo = new THREE.BoxGeometry(0.4 + rng() * 0.45, 0.01,
                                       0.3 + rng() * 0.3);
      lo.rotateY((rng() - 0.5) * 0.9);
      lo.translate(0, 0, side * (PATH_W / 2 + rng() * 0.18));
      lo.rotateY(yaw);
      lo.translate(mx + tx * (rng() - 0.5) * L, 0.005 + rng() * 0.005,
                   mz + tz * (rng() - 0.5) * L);
      b.add(lo, rng() < 0.6 ? MATS.concreteDark : MATS.stone,
            { collider: null, ao: 0 });
    }

    // loose stones along the margin
    if (rng() < 0.65) {
      const side = rng() < 0.5 ? -1 : 1;
      const off = side * (PATH_W / 2 + 0.08 + rng() * 0.35);
      const along = (rng() - 0.5) * L;
      b.rock(mx + tx * along + nx * off, -0.02, mz + tz * along + nz * off,
             0.04 + rng() * 0.045, MATS.stone, { collider: null });
    }
  }
}

// ---------------------------------------------------------------------------
// A mature round-crowned tree (the reference shows maples, not conifers).
// Tapered leaning trunk, a couple of branch stubs reaching into the canopy,
// and a crown of 3-5 overlapping squashed spheres at low poly counts.
function pickLeaf(rng) {
  const t = rng();
  return t < 0.5 ? MATS.leaf : t < 0.8 ? MATS.leafDark : MATS.leafWarm;
}

function tree(b, rng, x, z) {
  const h = 5 + rng() * 4;                       // 5..9 m overall
  const R = h * 0.22 + 0.25;                     // crown scale
  // the crown occupies the top of the tree, so the trunk climbs to h - 1.5R
  // and the canopy top lands close to the nominal height
  const trunkH = Math.max(h - R * (1.4 + rng() * 0.25), h * 0.35);
  const r0 = 0.13 + h * 0.017;
  const leanX = (rng() - 0.5) * 0.16;            // each tree leans its own way
  const leanZ = (rng() - 0.5) * 0.16;
  const bark = rng() < 0.45 ? MATS.barkDark : MATS.bark;

  const tg = new THREE.CylinderGeometry(r0 * 0.55, r0, trunkH, 8);
  tg.translate(0, trunkH / 2, 0);
  tg.rotateX(leanX);
  tg.rotateZ(leanZ);
  tg.translate(x, 0, z);
  // Collider on the trunk only, stopping just under the crown -- the canopy
  // must stay passable or the cat bumps into invisible leaves.
  b.add(tg, bark, {
    tile: 0,
    collider: { x, y: 0, z, w: r0 * 2, h: trunkH * 0.9, d: r0 * 2 },
  });

  // where the leaning trunk actually tops out
  const topX = x - Math.sin(leanZ) * trunkH;
  const topZ = z + Math.sin(leanX) * trunkH;

  const stubs = 2 + (rng() * 2 | 0);
  for (let s = 0; s < stubs; s++) {
    const L = h * (0.14 + rng() * 0.08);
    const sg = new THREE.CylinderGeometry(r0 * 0.16, r0 * 0.3, L, 5);
    sg.translate(0, L / 2, 0);
    sg.rotateZ(0.6 + rng() * 0.5);
    sg.rotateY(rng() * Math.PI * 2);
    sg.translate(topX, trunkH * (0.8 + rng() * 0.15), topZ);
    b.add(sg, bark, { tile: 0 });
  }

  const main = pickLeaf(rng);
  const blobs = 3 + (rng() * 3 | 0);             // 3..5
  const cy = trunkH + R * 0.45;
  for (let i = 0; i < blobs; i++) {
    const first = i === 0;
    const br = first ? R : R * (0.5 + rng() * 0.25);
    const a = rng() * Math.PI * 2;
    const off = first ? 0 : R * (0.5 + rng() * 0.35);
    let bx = topX + Math.cos(a) * off;
    let bz = topZ + Math.sin(a) * off;
    // keep crowns inside the inspector's bounds pad at the map edges
    bx = Math.min(Math.max(bx, BOUNDS.minX + br - 2), BOUNDS.maxX - br + 2);
    bz = Math.max(bz, BOUNDS.minZ + br - 2);
    const by = cy + (first ? 0 : (rng() - 0.3) * R * 0.55);
    const squash = 0.72 + rng() * 0.16;
    const mat = first || rng() < 0.7 ? main : pickLeaf(rng);
    b.sphere(bx, by - br * squash, bz, br, mat,
             { segments: 7, rings: 5, squash, solid: false });
  }

  // a drift of fallen leaves at the foot of about half the trees
  if (rng() < 0.5) {
    const dr = 0.7 + rng() * 0.7;
    b.sphere(x + (rng() - 0.5) * 1.4, -0.05, z + (rng() - 0.5) * 1.4, dr,
             MATS.leafWarm, { segments: 6, rings: 4, squash: 0.07 });
  }
}

// ---------------------------------------------------------------------------
// Understorey scatter pieces.  None of these carry colliders: shrubs and
// tufts the cat pushes through read far better than invisible walls.

function shrubClump(b, rng, x, z) {
  const n = 2 + (rng() * 3 | 0);
  const mat = rng() < 0.5 ? MATS.hedge : MATS.leafDark;
  for (let i = 0; i < n; i++) {
    const r = 0.35 + rng() * 0.5;
    b.sphere(x + (rng() - 0.5) * 1.4, -0.04, z + (rng() - 0.5) * 1.2, r, mat,
             { segments: 6, rings: 4, squash: 0.62 + rng() * 0.2 });
  }
}

/**
 * THE burr bush -- the one plant in the game that snags a cat.
 *
 * Exported, and every level module that wants a burred bush calls this and
 * nothing else (shed.js, fences.js).  That is a fairness rule wearing a
 * function signature: a hazard is only a fair hazard if it is unmistakable
 * BEFORE it is touched, and unmistakable means learned once -- one plant,
 * one silhouette, one colour, wherever it grows.  A module that drew its
 * own variant would be teaching the player a lie.
 *
 * Three tells, redundant on purpose so no lighting or background eats all
 * of them at once:
 *   - COLOUR: the dried rusty ochre of MATS.burr, worn by nothing else in
 *     the palette (materials.js argues the pick);
 *   - SILHOUETTE: a crown of near-black seed heads on stalks above the
 *     foliage, knobs where every living shrub here is a smooth dome;
 *   - TEXTURE: spiked all over with dead stems, prickly at any distance
 *     that renders it at all.
 *
 * The foliage dome is drawn ~0.7 m across -- deliberately WIDER than the
 * BURR_R = 0.55 snag disc in shared/constants.js, so the warning always
 * overhangs the trap and a brush past the outer leaves is a near miss, not
 * a snag.  No collider, like every other shrub back here: the burr is a
 * snare, never a wall, which is what guarantees it can never seal a route
 * or strand a happy place however carelessly one is placed (and is why
 * reach.mjs and nav.mjs stay untouched by planting one).
 *
 * The mark is INSIDE the draw and the draw is the only way to make one, so
 * a bush the server does not know about, or a snare with no bush over it,
 * cannot be authored.  server/src/burrs.js and game/src/burrs.js both read
 * the "burr:" markers off the one build; nobody keeps a second list.
 */
export function burrBush(b, rng, x, z, name) {
  b.mark("burr:" + name, x, 0, z);

  // the dried dome: two lobes so the outline is a plant, not a sphere
  b.sphere(x, -0.05, z, 0.42 + rng() * 0.06, MATS.burr,
           { segments: 7, rings: 5, squash: 0.6 + rng() * 0.1 });
  const a0 = rng() * Math.PI * 2;
  b.sphere(x + Math.cos(a0) * 0.26, -0.06, z + Math.sin(a0) * 0.26,
           0.3 + rng() * 0.06, MATS.burr,
           { segments: 6, rings: 4, squash: 0.62 });

  // dead stems bristling out of the dome.  Cones rotated outward through
  // raw geometry because b.cone only stands things upright, and upright
  // spikes read as grass; a burr bush bristles in every direction.
  const spikes = 8 + (rng() * 3 | 0);
  for (let i = 0; i < spikes; i++) {
    const len = 0.28 + rng() * 0.2;
    const g = new THREE.ConeGeometry(0.016 + rng() * 0.012, len, 4);
    g.translate(0, len / 2 + 0.12, 0);          // hinge above the dome's root
    g.rotateX(0.5 + rng() * 0.7);               // tipped well off vertical
    g.rotateY(rng() * Math.PI * 2);             // ...in its own direction
    g.translate(x, 0.1, z);
    b.add(g, MATS.burr, { tile: 0, collider: null });
  }

  // the seed heads: stalks up out of the crown, each topped with a dark
  // burr.  0.55-0.8 m up -- over every understorey dome, under every tree
  // crown, exactly the band a cat's-eye camera keeps against the sky.
  const heads = 5 + (rng() * 3 | 0);
  for (let i = 0; i < heads; i++) {
    const a = rng() * Math.PI * 2, d = rng() * 0.3;
    const hx = x + Math.cos(a) * d, hz = z + Math.sin(a) * d;
    const top = 0.55 + rng() * 0.25;
    b.cone(hx, 0.1, hz, 0.014, top - 0.1, MATS.burr, { segments: 4 });
    b.sphere(hx, top - 0.045, hz, 0.05 + rng() * 0.025, MATS.burrHead,
             { segments: 5, rings: 4 });
  }
}

function bramblePatch(b, rng, x, z) {
  const n = 3 + (rng() * 2 | 0);
  for (let i = 0; i < n; i++) {
    const r = 0.55 + rng() * 0.55;
    b.sphere(x + (rng() - 0.5) * 2.4, -0.05, z + (rng() - 0.5) * 1.6, r,
             MATS.leafDark, { segments: 6, rings: 4, squash: 0.3 + rng() * 0.12 });
  }
}

function grassTuft(b, rng, x, z) {
  // Blades, not pyramids.  A 0.2 m-wide cone standing 0.5 m tall is a third
  // the height of the cat and reads as a shrub; grass wants to be several thin
  // spikes at ankle height, which also lets the cat's silhouette stay legible
  // against it.
  const n = 4 + (rng() * 4 | 0);
  for (let i = 0; i < n; i++) {
    b.cone(x + (rng() - 0.5) * 0.34, 0, z + (rng() - 0.5) * 0.34,
           0.03 + rng() * 0.035, 0.16 + rng() * 0.2,
           rng() < 0.5 ? MATS.lawnDry : MATS.leaf, { segments: 4 });
  }
}

function wildflowers(b, rng, x, z) {
  b.sphere(x, -0.06, z, 0.55 + rng() * 0.3, MATS.leaf,
           { segments: 6, rings: 4, squash: 0.3 });
  const n = 5 + (rng() * 4 | 0);
  const mat = rng() < 0.5 ? MATS.flowerWarm : MATS.flowerPale;
  for (let i = 0; i < n; i++) {
    const a = rng() * Math.PI * 2, d = rng() * 0.55;
    b.sphere(x + Math.cos(a) * d, 0.14 + rng() * 0.12, z + Math.sin(a) * d,
             0.05 + rng() * 0.04, mat, { segments: 4, rings: 3 });
  }
}

// A fallen log, r 0.16..0.25 so its top sits 0.32..0.5 m up: a walkway the
// cat mounts in one hop.  Sometimes a snapped stump stands beside it.
function deadfall(b, rng, x, z, len, axis) {
  const r = 0.16 + rng() * 0.09;
  const mat = rng() < 0.5 ? MATS.bark : MATS.barkDark;
  b.bar(x, 0, z, r, len, axis, mat, { segments: 8 });
  if (rng() < 0.5) {
    const sx = x + (axis === "x" ? len / 2 + 0.5 : 0.6);
    const sz = z + (axis === "z" ? len / 2 + 0.5 : 0.45);
    b.post(sx, 0, sz, r * 1.15, 0.3 + rng() * 0.15, mat,
           { topRadius: r, segments: 8 });
  }
  return r;
}

// ---------------------------------------------------------------------------
// Park furniture, all axis-aligned so the visual boxes and the colliders are
// the same shape the cat lands on.

function signpost(b, x, z) {
  b.post(x, 0, z, 0.055, 2.1, MATS.timber, { topRadius: 0.05, segments: 7 });
  // the board's collider would float 1.5 m up with nothing to stand on
  b.block(x, 1.45, z + 0.03, 0.8, 0.45, 0.05, MATS.timber, { solid: false });
}

function bollard(b, x, z) {
  b.post(x, 0, z, 0.09, 0.68, MATS.timber, { topRadius: 0.08, segments: 8 });
}

function culvertMouth(b, rng, x, z) {
  b.bar(x, 0, z, 0.3, 0.9, "x", MATS.concreteDark, { segments: 9 });
  b.rock(x + (rng() - 0.5) * 0.6, 0, z + 0.75, 0.3 + rng() * 0.12, MATS.stone);
}

// ---------------------------------------------------------------------------
// The creek, weaving along the woods side of the trail.
//
// This used to be a lie, and the comment that stood here said so plainly: the
// ground was a flat slab the cat floored against at a fixed height, so a bed
// dug into it could be neither stood in nor even seen.  The creek was
// therefore faked UPWARD -- three stacked 8 mm ribbons of soil, shingle and
// MATS.glass (the house-window material, borrowed for want of anything wetter)
// sitting 16 mm ABOVE the lawn between two 0.1 m berms pretending to be banks.
// It read as a blue rectangle lying in the grass, because that is what it was.
//
// spec.groundY carves the channel for real now, so every one of those props is
// gone.  What is left here is only the part a height function cannot express:
// the water surface, the dark lining of the bed beneath it, the wet margin
// above it, and the things standing in it.
//
// One rule holds all of it together: ASK THE TERRAIN.  The channel's depth and
// half-width both vary along x, so any fixed offset from creekZ() quietly
// rebuilds the constant-width ribbon this replaces.  Nothing below measures
// out from the centre line; every edge is found by sampling groundY, and only
// the *bracket* for that search comes from CREEK.halfWidth, which is the pool
// maximum and so is guaranteed to reach past the bank wherever we look.

/**
 * How far out from the centre line, on `side`, the bed rises through `y`.
 *
 * The workhorse.  Over a gravel bar the wetted width pinches to a third of a
 * metre and in a pool it spreads past half; bisecting groundY finds the edge
 * wherever it actually is, and goes on working if the terrain track adds
 * relief nobody here has heard of.  Returns 0 where the station is dry and the
 * full bracket where the channel is brimming, so callers never get a
 * meaningless answer back.
 */
function shoreOffset(x, y, side) {
  const zc = creekZ(x);
  let lo = 0, hi = CREEK.halfWidth;
  if (groundY(x, zc) >= y) return 0;
  if (groundY(x, zc + side * hi) < y) return hi;
  for (let k = 0; k < 14; k++) {                 // ~0.1 mm, far past visible
    const m = (lo + hi) / 2;
    if (groundY(x, zc + side * m) < y) lo = m; else hi = m;
  }
  return (lo + hi) / 2;
}

// Stations along the creek, and rows across it.  0.5 m in x sits well inside
// the shortest wobble in creekZ (~18 m), and 14 rows put a sample line every
// 0.25 m across the widest the channel ever gets.  Neither wants to be finer:
// the clip below interpolates *within* a cell, so the shoreline's accuracy is
// set by how curved groundY is across a cell, not by how big the cell is.
const CH_DX = 0.5;
const CH_ROWS = 14;

// The water's edge is biased a few millimetres below the true waterline.  Our
// shoreline and the terrain mesh's own are both piecewise-linear but sampled
// on different grids, so exactly at the contour the water can poke out past
// the bed and hang in the air.  Clipping a hair short tucks that last
// centimetre under the bank instead, where the seam cannot be seen.
const EDGE_BIAS = 0.005;

/** Keep the part of a convex cell polygon where field `f` is positive. */
function clipPositive(poly, f) {
  const out = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], c = poly[(i + 1) % poly.length];
    const fa = a.f[f], fc = c.f[f];
    if (fa > 0) out.push(a);
    if ((fa > 0) !== (fc > 0)) {
      const t = fa / (fa - fc);
      out.push({
        x: a.x + (c.x - a.x) * t,
        y: a.y + (c.y - a.y) * t,
        z: a.z + (c.z - a.z) * t,
        f: a.f.map((v, j) => v + (c.f[j] - v) * t),
      });
    }
  }
  return out;
}

/**
 * Triangles for a surface over the channel, kept only where every field is
 * positive.
 *
 * This is the piece of the job that earns the rest.  The water is a PLANE at
 * one absolute height clipped to where the bed lies under it, so the shoreline
 * is wherever the terrain happens to rise through CREEK.waterY -- it spreads
 * in the pools, pinches over the bars, and leaves the centre line on its own
 * without anyone drawing that.  Emitting a fixed-width strip is precisely the
 * bug this exists to not have.
 *
 * The grid is curvilinear -- columns along x, rows spanning rim to rim about
 * creekZ(x) -- so it wraps the meander in a couple of thousand cells instead
 * of blanketing 68 x 16 m of parkland to find a creek in it.  Cells are cut
 * one field at a time by a convex polygon walk against the f = 0 contour,
 * which is why a *band* between two contours -- the damp margin -- costs
 * nothing but a second field.  Attributes ride through the crossings, so the
 * margin's height interpolates along the bank instead of stepping.
 */
function channelTris(fields, heightAt) {
  const N = Math.ceil((BOUNDS.maxX - BOUNDS.minX) / CH_DX);
  const cols = [];
  for (let i = 0; i <= N; i++) {
    const x = BOUNDS.minX + ((BOUNDS.maxX - BOUNDS.minX) * i) / N;
    const zc = creekZ(x);
    const col = [];
    for (let k = 0; k <= CH_ROWS; k++) {
      const z = zc + ((2 * k) / CH_ROWS - 1) * CREEK.halfWidth;
      col.push({ x, y: heightAt(x, z), z, f: fields.map((fn) => fn(x, z)) });
    }
    cols.push(col);
  }

  const pos = [], nrm = [];
  const u = new THREE.Vector3(), v = new THREE.Vector3(), n = new THREE.Vector3();
  for (let i = 0; i < N; i++) {
    for (let k = 0; k < CH_ROWS; k++) {
      // Wound counter-clockwise seen from above -- +x along the far row, then
      // back across -z -- so every face this emits looks up.
      let poly = [cols[i][k + 1], cols[i + 1][k + 1], cols[i + 1][k], cols[i][k]];
      for (let f = 0; f < fields.length && poly.length > 2; f++) {
        poly = clipPositive(poly, f);
      }
      for (let t = 1; t + 1 < poly.length; t++) {
        const p = [poly[0], poly[t], poly[t + 1]];
        u.set(p[1].x - p[0].x, p[1].y - p[0].y, p[1].z - p[0].z);
        v.set(p[2].x - p[0].x, p[2].y - p[0].y, p[2].z - p[0].z);
        n.crossVectors(u, v);
        // Clipping routinely leaves slivers where a contour grazes a corner.
        // They cover nothing and their normals are pure noise, so drop them
        // rather than merge a NaN into the batch.
        if (n.lengthSq() < 1e-12) continue;
        n.normalize();
        for (const q of p) { pos.push(q.x, q.y, q.z); nrm.push(n.x, n.y, n.z); }
      }
    }
  }
  return { pos, nrm };
}

function surfaceGeo({ pos, nrm }) {
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(nrm, 3));
  return g;
}

function waterSurface(b) {
  const tris = channelTris(
    [(x, z) => CREEK.waterY - EDGE_BIAS - groundY(x, z)],
    () => CREEK.waterY);
  if (!tris.pos.length) return;
  // tint and ao off: shade() darkens a piece toward its own lowest point, and
  // a plane has no lowest point to darken toward, so with the defaults the
  // whole creek comes back one flat 28% darker than the material asked for --
  // and one random tint step darker again than the creek next door.
  b.add(surfaceGeo(tris), MATS.water,
        { transparent: true, collider: null, tile: 1, tint: 0, ao: 0 });
}

/**
 * The bed, lined dark beneath the water.
 *
 * The terrain track paints the channel in dry `soil` and pale shingle, and
 * at 0.8 opacity a fifth of whatever lies behind the surface is blended into
 * every water pixel.  Sunlit soil is bright and warm, and a fifth of bright
 * warm brown is enough to drag any blue the material asks for back toward
 * olive -- which is why two rounds of retinting the water never fixed it
 * (materials.js tells that story).  Covering the wetted bed with `wetSoil`
 * makes what bleeds through dim and close to neutral instead, and it is also
 * simply what the bed IS: soil under standing water is soaked, dark, and far
 * less rough than the dry bank above it.
 *
 * ao is pulling the same trick the damp margin's does, pointed the other
 * way.  shade() darkens toward the lowest point of the piece, and this
 * piece's ceiling is the flat waterline while its floor is the bottom of the
 * deepest pool -- so the darkening IS a depth map.  The bed fades toward
 * black under the pools, where the water above it goes dark and blue, and
 * stays pale through the last few centimetres at the shore, where seeing the
 * bottom is what says "shallow".  A depth tint on the water itself would
 * take a shader; this buys it with vertex colours.
 */
function wetBed(b) {
  const tris = channelTris(
    [(x, z) => CREEK.waterY - EDGE_BIAS - groundY(x, z)],
    (x, z) => groundY(x, z) + 0.008);
  if (!tris.pos.length) return;
  // 8 mm proud of the terrain like the damp margin, and clipped against the
  // same biased contour from the other side: both fields cross zero at the
  // same points of the same grid, so the two overlays share their crossing
  // vertices exactly and meet at the waterline without a crack.  Tile 0.56
  // is the margin's 0.8 scaled by the two maps' own repeats (14 against 20),
  // so the grain does not step across the seam.
  b.add(surfaceGeo(tris), MATS.wetSoil,
        { collider: null, tile: 0.56, tint: 0, ao: 0.6 });
}

// The rise above the waterline that still reads as wet.  Deliberately a
// height and not a width: the band is as wide as this much climb takes, so it
// is a hand's breadth up a steep bank and spreads right out across a shallow
// riffle -- which is how a wet margin actually behaves, and something no
// constant-width ribbon can do.
const DAMP_RISE = 0.09;

function dampMargin(b) {
  const tris = channelTris([
    (x, z) => groundY(x, z) - (CREEK.waterY - EDGE_BIAS),
    (x, z) => CREEK.waterY + DAMP_RISE - groundY(x, z),
  ], (x, z) => groundY(x, z) + 0.008);
  if (!tris.pos.length) return;
  // The bed material belongs to the terrain track, so this is an overlay and
  // not a re-surfacing: 8 mm proud of groundY, sampled at every vertex, so it
  // follows the bank instead of hovering over it like a flat box would.
  //
  // ao is doing the real work.  shade() darkens toward the lowest point of the
  // piece, and the lowest point of this one IS the waterline -- so the band
  // arrives soaked at the water's edge and drying as it climbs, a gradient the
  // material cannot carry and the shadow map is orders of magnitude too coarse
  // to give.
  b.add(surfaceGeo(tris), MATS.soil,
        { collider: null, tile: 0.8, tint: 0.05, ao: 0.5 });
}
/**
 * Register the water's ripples with the level's per-frame hook.
 */
function driftWater(b) {
  // The ripple speeds and the numerics live with the material, in
  // materials.js, next to the two maps they drive -- there is no second
  // opinion about how fast the creek runs.  What belongs HERE is the
  // decision to run it at all: register through the builder and the ripples
  // tick only in a build that actually laid water.
  if (MATS.water.normalMap || MATS.water.clearcoatNormalMap) b.onUpdate(tickWater);
}

// A stone on the bed.  b.rock() sinks its boulder on the way in -- an
// icosahedron of radius r, squashed to 0.72 in y and centred at 0.52 r -- so
// the top lands 1.24 r above the y it is handed.  Knowing that is what lets a
// stone be *sized* to break the surface or to stay under it, instead of being
// scattered at a guessed radius and hoping.  Both kinds matter: the proud ones
// are what tell you the water is shallow, and the drowned ones are what tell
// you it has a bottom.
function bedStone(b, rng, x, zc) {
  const side = rng() < 0.5 ? -1 : 1;
  const wet = shoreOffset(x, CREEK.waterY, side);
  // Which kind it is decides where it goes, not the other way round: a stone
  // meant to vanish has to be put somewhere with enough water to hide it, and
  // the shallows near the waterline -- where most of a uniform scatter lands,
  // because that is where most of the wetted width is -- have a couple of
  // centimetres at best.  Drowned ones keep to the deep middle; proud ones
  // range right out over the bar and past it.
  const drown = rng() < 0.4;
  const z = zc + side * wet * (drown ? rng() * 0.55 : 0.2 + rng() * 1.0);
  const depth = CREEK.waterY - groundY(x, z);
  let r;
  if (depth <= 0.02) {
    r = 0.05 + rng() * 0.09;                             // dry, on the shingle
  } else if (drown) {
    r = Math.max(0.03, (depth - 0.02) / 1.24) * (0.6 + rng() * 0.4);
  } else {
    r = (depth + 0.025) / 1.24 + rng() * 0.06;           // breaks the surface
  }
  b.rock(x, 0, z, r, MATS.stone, { collider: null });
}

// Reeds.  They used to stand a flat 1.0..1.45 m out from the centre line,
// which against a real channel is halfway up the bank and out of the water
// entirely -- reeds do not grow on a dry slope, they grow with their feet wet.
// Rather more than half of them stand IN the shallows now, and those are grown
// tall enough to clear the water they happen to be standing in: a fixed height
// would leave the ones in a pool as stubs poking through the surface.
function reeds(b, rng, x, zc) {
  const side = rng() < 0.5 ? -1 : 1;
  const wet = shoreOffset(x, CREEK.waterY, side);
  const off = rng() < 0.6
    ? wet * (0.3 + rng() * 0.65)                         // in the water
    : Math.min(wet + 0.12 + rng() * 0.6, CREEK.halfWidth);
  const cx = x + (rng() - 0.5) * 0.5;
  const cz = zc + side * off;
  const sunk = Math.max(0, CREEK.waterY - groundY(cx, cz));
  const n = 3 + (rng() * 3 | 0);
  for (let k = 0; k < n; k++) {
    b.cone(cx + (rng() - 0.5) * 0.3, 0, cz + (rng() - 0.5) * 0.3,
           0.018 + rng() * 0.02, sunk + 0.26 + rng() * 0.3,
           rng() < 0.6 ? MATS.leaf : MATS.lawnDry, { segments: 4 });
  }
}

function creek(b, rng) {
  // The water, its bed lining and its margin are the only things back here
  // quoted in absolute world y: CREEK.waterY is a level, not a height above
  // anything.  The module grade is groundY, and the Builder would offset each
  // of them by the depth of the bed beneath their own bounding-box centre --
  // a plane already at -1.07 dropped another 1.2 m into the dark.  Off for
  // those three, and back on for everything that genuinely stands on the bed
  // and wants finding.
  const grade = b.grade;
  b.setGrade(null);
  wetBed(b);
  waterSurface(b);
  dampMargin(b);
  b.setGrade(grade);

  // Stones and reeds are stationed along the creek at the same 1.25 m spacing
  // the trail uses.  Note there is no cross-channel normal any more: both
  // place themselves as a fraction of the wetted half-width measured out along
  // z from creekZ, and shoreOffset measures along z too, so the two agree by
  // construction and a point at `wet` is exactly on the waterline.
  const step = 1.25;
  for (let x0 = BOUNDS.minX; x0 < BOUNDS.maxX; x0 += step) {
    const mx = Math.min(x0 + step / 2, BOUNDS.maxX);
    const zc = creekZ(mx);
    if (rng() < 0.62) bedStone(b, rng, mx, zc);
    if (rng() < 0.42) reeds(b, rng, mx, zc);
  }

  // The two fallen branches, kept -- but where they sit matters now that the
  // ground under them is not flat.  A piece takes its height from the terrain
  // beneath its own centre and does not tilt with it, so a branch centred over
  // the channel would lie flat on the bed, drowned.  Centre them on the bank
  // and their length reaches out over the water at bank height instead, which
  // is what a fallen branch does.
  const wx = -6.5;
  b.bar(wx, 0, creekZ(wx) - 1.1, 0.07, 2.8, "z", MATS.barkDark, { segments: 7 });
  // this one lies along the water's edge, found rather than assumed
  const ex = 23.5;
  b.bar(ex, 0, creekZ(ex) + shoreOffset(ex, CREEK.waterY + 0.04, 1),
        0.09, 2.9, "x", MATS.bark, { segments: 7 });

  driftWater(b);
}

/**
 * A crossing.
 *
 * Each stone is a plug rising from the bed it actually stands on to a top just
 * clear of the water, so its height is (waterY + freeboard - groundY) sampled
 * at its own position.  The old ones were a flat 0.09..0.12 m tall on the
 * assumption of a lawn at y = 0; with the bed now cut below the water every
 * one of them would be drowned, and a crossing sited on a riffle wants much
 * shorter stones than one in a pool in any case.  Nothing here is told which
 * it is standing in -- it asks.
 *
 * The numbers exist so that a crossing is WALKED, not jumped.  Every top lands
 * within a few centimetres of its neighbours and of the bank beside it, well
 * inside MOVE.stepHeight -- which is 0.23, not the 0.13 the comment that used
 * to sit here claimed.  That figure was stale, and it had these stones sized
 * for a clumsier cat than the game has ever shipped.
 *
 * The run spans the wetted width found at that station rather than a fixed
 * spread, and carries as many stones as it takes to keep consecutive colliders
 * closer together than the cat is wide -- so its box always overlaps one and
 * it is never left unsupported over open water.  Over a metre and a half of
 * creek that comes out as five stones nearly touching rather than three with
 * strides between them, which is the honest answer for an animal 0.24 m
 * across: a gap it has to clear is a gap it has to jump.
 */
const STONE_STRIDE = 0.50;                       // centre to centre, at most
const STONE_GAP = MOVE.halfWidth;                // open water between colliders
const STONE_JITTER = 0.03;                       // wander along the crossing

function steppingStones(b, rng, x) {
  const zc = creekZ(x);
  // start and finish on dry ground a stone's width beyond the water
  const z0 = zc - shoreOffset(x, CREEK.waterY, -1) - 0.14;
  const z1 = zc + shoreOffset(x, CREEK.waterY, 1) + 0.14;
  const n = Math.max(3, Math.ceil((z1 - z0) / STONE_STRIDE) + 1);
  const stride = (z1 - z0) / (n - 1);
  // The collider is r * 1.5 across -- deliberately narrower than the stone, so
  // nothing here is standable where it does not look standable.  This is the
  // radius that closes the gaps to STONE_GAP with both stones at the small end
  // of their size range and both jittered apart; clamped either side so they
  // stay stones rather than pebbles or paving.
  const r0 = Math.min(0.34, Math.max(0.18,
    (stride + 2 * STONE_JITTER - STONE_GAP) / (1.5 * 0.94)));

  for (let s = 0; s < n; s++) {
    const sx = x + (rng() - 0.5) * 0.28;
    const sz = z0 + stride * s + (rng() - 0.5) * STONE_JITTER * 2;
    const r = r0 * (0.94 + rng() * 0.14);
    // Freeboard is small on purpose: it is also the step up from the bank at
    // the water's edge, and that step has to stay inside MOVE.stepHeight.
    const top = CREEK.waterY + 0.05 + rng() * 0.03;
    const h = Math.max(top - groundY(sx, sz), 0.06);
    const g = new THREE.CylinderGeometry(r * 0.86, r, h, 7);
    g.rotateY(rng() * Math.PI);
    g.translate(sx, h / 2, sz);
    // y: 0 in both, so the module grade drops mesh and collider onto the bed
    // together -- these are the creek's only colliders and the one thing here
    // that has to be exactly where it looks.
    b.add(g, MATS.stone, {
      tile: 0,
      collider: { x: sx, y: 0, z: sz, w: r * 1.5, h, d: r * 1.5 },
    });
  }
  return zc;
}

// ---------------------------------------------------------------------------

export function build(b) {
  const rng = makeRng(0x6ee7c47);

  // occupancy list so scattered pieces do not sit inside each other
  const placed = [];
  const blocked = (x, z, need) =>
    placed.some((p) => (p.x - x) ** 2 + (p.z - z) ** 2 < (p.r + need) ** 2);
  const nearPath = (x, z, d) => Math.abs(z - greenPathZ(x)) < d;
  // Keep-outs are quoted as a depth of cut rather than a distance from the
  // centre line (see cutAt).  0.03 stops a trunk just short of where the bank
  // starts to fall away, which is what leaves a tree leaning out over the
  // water instead of standing back from it or standing in it; 0.22 is a
  // hand's breadth above the waterline at 0.27, so grass runs right down to
  // where it gets wet and stops.
  const inCreek = (x, z, drop) => cutAt(x, z) > drop;

  // ---- ground dressing, laid before the path so it shows at the edges ----
  // worn dirt where feet have cut the corners
  for (const wx of [-27, -8, 9, 26]) {
    b.slab(wx, 0.002, greenPathZ(wx), 2.6 + rng() * 1.4, 2.3 + rng() * 0.8,
           MATS.soil, { thickness: 0.012 });
  }
  // a desire line from the middle lot's back fence down to the path
  b.slab(0, 0.002, -33.4, 0.8, 6.0, MATS.soil, { thickness: 0.012 });
  // shallow drainage swale along the very back, fed by a culvert at each end
  b.slab(0, 0.002, -45.0, BOUNDS.maxX - BOUNDS.minX - 1, 1.5, MATS.soil,
         { thickness: 0.012 });
  culvertMouth(b, rng, -33.3, -45.0);
  culvertMouth(b, rng, 33.3, -45.0);

  gravelPath(b, rng);
  creek(b, rng);

  // ---- furniture along the trail (garden side, clear of the creek) ----
  signpost(b, -30.5, greenPathZ(-30.5) + 1.4);
  bollard(b, 33.0, greenPathZ(33.0) + 1.05);   // flank the path at the east end
  bollard(b, 33.0, greenPathZ(33.0) - 1.05);
  // No entry for the litter bin any more: it is gone, and an exclusion with
  // nothing inside it is just a bald patch in the scatter that nobody can
  // explain from looking at it.
  placed.push({ x: -30.5, z: greenPathZ(-30.5) + 1.4, r: 0.6 },
              { x: 0, z: -33.4, r: 1.2 }, { x: 0, z: -35.8, r: 1.2 });

  b.mark("greenPathWest", BOUNDS.minX + 1, 0, greenPathZ(BOUNDS.minX + 1));
  b.mark("greenPathEast", BOUNDS.maxX - 1, 0, greenPathZ(BOUNDS.maxX - 1));
  b.mark("greenCreekWest", BOUNDS.minX + 1, 0, creekZ(BOUNDS.minX + 1));
  b.mark("greenCreekEast", BOUNDS.maxX - 1, 0, creekZ(BOUNDS.maxX - 1));

  // three crossings, spread so no stretch of creek is a long detour
  for (const [tag, cx] of [["W", -20], ["M", 2], ["E", 17]]) {
    const zc = steppingStones(b, rng, cx);
    b.mark("greenCreekSteps" + tag, cx, 0, zc);
    placed.push({ x: cx, z: zc, r: 1.0 });
  }

  // ---- burr bushes -- the greenspace's share of the level's snag hazard ----
  // Hand-placed, never scattered, because every position carries a safety
  // argument the rng cannot make: each stands >= 2.5 m off the trail centre,
  // outside every happy-place disc, and clear of the straight line a brain
  // walks from the rear-fence hop (0.4, -30) to the creek crossing (2, -38.9)
  // -- so the cats that cannot see (the brains) only ever meet one while
  // wandering, and the snag frees them on its own clock (player.js).  The
  // bushes draw on their own rng stream so planting them did not reshuffle
  // every tree in the wood, but they push into `placed` BEFORE the scatter
  // loops run: a green shrub grown into a burr bush would blur the one
  // silhouette the player is being taught to trust, so the scatter is told
  // to keep clear.
  const burrRng = makeRng(0xb1122ed);
  for (const [tag, bx, bz] of [
    // flanking the open grass west of the garden gate's desire line, in
    // plain sight on the walk down to the creek -- the teaching pair
    ["green-trailW", -5.0, -35.4],
    ["green-trailE", 12.5, -31.9],
    // the wild back edge, bramble country, where pushing off-trail is
    // supposed to need a little care
    ["green-backW", -12.5, -45.2],
    ["green-backE", 22.5, -41.0],
    // open grass by the west fence line
    ["green-west", -26.0, -34.5],
    // 1.5 m outside the butterfly clearing's rim: the one deliberate
    // risk/reward placement.  A pounce aimed carelessly OUT of the clearing
    // can land in it; standing anywhere inside the zone cannot reach it.
    // Brains never visit (butterflies is a needs-you place).
    ["green-butterfly", -14.5, -31.8],
  ]) {
    rooted(b, bx, bz, () => burrBush(b, burrRng, bx, bz, tag));
    placed.push({ x: bx, z: bz, r: 1.1 });
  }

  // ---- deadfall: a run along the wild back edge, two more near the path ----
  for (const [lx, ljit] of [[-28, 0], [-17, 0.6], [-3, -0.4], [12, 0.5], [24, 0]]) {
    const lz = -44.2 + ljit + (rng() - 0.5) * 0.8;
    deadfall(b, rng, lx, lz, 2.6 + rng() * 1.5, rng() < 0.25 ? "z" : "x");
    placed.push({ x: lx, z: lz, r: 1.6 });
  }
  const nearLogs = [[5, greenPathZ(5) - 2.3], [-22, greenPathZ(-22) + 2.3]];
  for (const [lx, lz] of nearLogs) {
    deadfall(b, rng, lx, lz, 2.8, "x");
    placed.push({ x: lx, z: lz, r: 1.6 });
  }

  // ---- trees: a dense back rank opening out to a few park specimens ----
  let treeCount = 0;
  for (let n = 0, tries = 0; n < 24 && tries < 500; tries++) {
    const x = -31.5 + rng() * 63;
    const z = -44.9 + rng() * 4.1;               // the woodland band
    if (nearPath(x, z, 2.4) || inCreek(x, z, 0.03) || blocked(x, z, 1.15)) continue;
    rooted(b, x, z, () => tree(b, rng, x, z));
    placed.push({ x, z, r: 0.95 });
    n++; treeCount++;
  }
  for (let n = 0, tries = 0; n < 11 && tries < 500; tries++) {
    const x = -31.5 + rng() * 63;
    const z = -39.8 + rng() * 7.6;               // scattered parkland trees
    if (nearPath(x, z, 2.6) || inCreek(x, z, 0.03) || blocked(x, z, 1.7)) continue;
    rooted(b, x, z, () => tree(b, rng, x, z));
    placed.push({ x, z, r: 0.95 });
    n++; treeCount++;
  }

  // ---- understorey, densest where the trees are ----
  for (let n = 0, tries = 0; n < 15 && tries < 300; tries++) {
    const back = rng() < 0.7;
    const x = -32 + rng() * 64;
    const z = back ? -45.2 + rng() * 3.8 : -40 + rng() * 8.5;
    if (nearPath(x, z, 1.9) || inCreek(x, z, 0.10) || blocked(x, z, 0.05)) continue;
    rooted(b, x, z, () => shrubClump(b, rng, x, z));
    n++;
  }
  for (let n = 0, tries = 0; n < 7 && tries < 200; tries++) {
    const x = -31 + rng() * 62;
    const z = -45.2 + rng() * 2.2;
    if (nearPath(x, z, 2.0) || inCreek(x, z, 0.06)) continue;
    rooted(b, x, z, () => bramblePatch(b, rng, x, z));
    n++;
  }
  for (let n = 0, tries = 0; n < 34 && tries < 400; tries++) {
    const x = -33 + rng() * 66;
    const z = -45.3 + rng() * 14.4;
    if (nearPath(x, z, 1.1) || inCreek(x, z, 0.22)) continue;
    grassTuft(b, rng, x, z);
    n++;
  }
  for (let n = 0, tries = 0; n < 6 && tries < 200; tries++) {
    const x = -30 + rng() * 60;
    const z = -34.6 + rng() * 3.4;               // the open grass by the fence
    if (nearPath(x, z, 1.6) || inCreek(x, z, 0.03) || blocked(x, z, 0.5)) continue;
    wildflowers(b, rng, x, z);
    n++;
  }

  // a few boulders where the swale gets rocky
  for (const [rx, rz, rr] of [[-9, -44.6, 0.42], [18.5, -44.9, 0.34], [29, -43.6, 0.3]]) {
    if (!blocked(rx, rz, 0.4)) b.rock(rx, 0, rz, rr, MATS.stone);
  }
}
